import { TweenMax, TimelineMax, Power3 } from 'gsap/TweenMax';
import Hammer from 'hammerjs';

import store from '@/store';
import router from '@/router';
import Navigator from './Navigator';
import Cursor from './Cursor';
import YearSlider from './YearSlider';
import scrollbar from './scrollbar';
import Stage from './stage';
import loop from './loop';
import eventHub from './eventHub';
import CoreGroup from './stage/CoreGroup';
import { isTouchDevice } from '@/scripts/utils';
import animations from './animations';


class Interface {
  constructor() {
    this.years = [1990, 1997, 1998, 2001, 2004, 2010, 2011, 2018, 2019];
    this.prevIndex = null;
    this.currentIndex = null;
    this.currentYear = null;
    this.hm = null;
  }

  init(canvas, navigator, cursor, yearSlider) {
    this.navigator = new Navigator(navigator);
    this.cursor = Cursor;
    this.cursor.init(cursor);
    this.stage = new Stage(canvas);
    this.yearSlider = new YearSlider(yearSlider);
    if (isTouchDevice()) this.hm = new Hammer(document.querySelector('.app > main'));
    this.year = document.querySelector('.app__year');
    this.coreGroup = CoreGroup;

    this.totalLength = this.years.length - 1;

    const handlerRender = time => this.render(time);
    loop.add(handlerRender, 'stage_render');

    this.setInitialState();
  }

  setInitialState() {
    if (store.state.route.name === 'home' || store.state.route.name === 'errorPage') {
      this.prevIndex = 0;
      this.currentIndex = 0;
      this.currentYear = { value: this.years[this.currentIndex] };
    } else if (store.state.route.name === 'buyNow') {
      this.prevIndex = this.totalLength - 1;
      this.currentIndex = this.totalLength;
      this.currentYear = { value: this.years[this.currentIndex] };

      this.navigator.animateProgress(this.currentIndex - 1);
    } else if (store.state.route.name === 'page') {
      const currentIndex = this.years.findIndex(
        numb => numb === parseInt(store.state.route.params.year, 10),
      );
      this.prevIndex = currentIndex - 1;
      this.currentIndex = currentIndex;
      this.currentYear = { value: this.years[this.currentIndex] };

      this.navigator.animateProgress(this.currentIndex - 1);
    }

    this.cursor.toNormal();

    store.commit('SET_CURRENT_INDEX', this.currentIndex);
    this.yearSlider.setIndex(this.currentIndex);
    this.yearSlider.init();
  }

  afterLoading() {
    this.addEvents();
    this.coreGroup.init();
  }

  addEvents() {
    const handlerResize = () => this.resize();
    eventHub.$on('windowResize', handlerResize);

    const handlerToIndex = () => this.toIndex();
    eventHub.$on('toIndex', handlerToIndex);

    const handlerScrollTo = i => this.scrollTo(i);
    eventHub.$on('scrollTo', handlerScrollTo);

    const handlerNavigateTo = i => this.navigateTo(i);
    eventHub.$on('navigateTo', handlerNavigateTo);

    eventHub.$on('cursorToZoom', () => this.cursor.toZoom());

    eventHub.$on('cursorToScroll', () => this.cursor.toNormal());

    eventHub.$on('cursorToClose', () => this.cursor.toClose());

    eventHub.$on('cursorToDot', () => this.cursor.toDot());

    this.handlerKeydown = event => this.onKeydown(event);
    window.addEventListener('keydown', this.handlerKeydown);

    const handlerTiltCaption = text => this.cursor.tiltCaption(text);
    eventHub.$on('titltCursorCaption', handlerTiltCaption);

    const handlerMousedown = event => this.onMousedown(event);
    eventHub.$on('mousedownPage', handlerMousedown);

    const handlerMouseup = () => this.onMouseup();
    eventHub.$on('mouseupPage', handlerMouseup);

    const handlerLockScroll = bool => this.lockScroll(bool);
    eventHub.$on('lockScroll', handlerLockScroll);

    const handlerSwipe = event => this.onSwipe(event);
    if (isTouchDevice()) this.hm.on('swipe', handlerSwipe);
    eventHub.$on('swipeClick', handlerSwipe);
  }

  changeIndexes(numb) {
    this.prevIndex = this.currentIndex;
    this.currentIndex = numb;
  }

  lockScroll(bool) {
    if (store.state.isInTransition) return;
    store.commit('SET_STATE_TRANSITION', true);
    store.commit('SET_STATE_LOCK_SCROLLING', bool);

    this.pageAnimation(bool ? 'leave' : 'enter');
  }

  onKeydown(event) {
    if (store.state.isInTransition) return;
    if (!store.state.scrollIsLocked) return;
    if (this.cursor.state === 'holding') return;

    if (event.keyCode === 39 && this.currentIndex < this.totalLength) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex + 1);
      this.transitionSlider();
    } else if (event.keyCode === 37 && this.currentIndex > 1) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex - 1);
      this.transitionSlider();
    }
  }

  onScroll(event) {
    if (store.state.isInTransition) return;
    if (!store.state.scrollIsLocked) return;
    if (this.cursor.state === 'holding') return;

    if (event.detail.delta.y > 10 && this.currentIndex < this.totalLength) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex + 1);
      this.transitionSlider();
    } else if (event.detail.delta.y < -10 && this.currentIndex > 1) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex - 1);
      this.transitionSlider();
    }
  }

  onSwipe(event) {
    if (store.state.isInTransition) return;
    if (!store.state.scrollIsLocked) return;

    if (event.direction === 2 && this.currentIndex < this.totalLength) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex + 1);
      this.transitionSlider();
    } else if (event.direction === 4 && this.currentIndex > 1) {
      store.commit('SET_STATE_TRANSITION', true);
      this.changeIndexes(this.currentIndex - 1);
      this.transitionSlider();
    }
  }

  onMousedown(event) {
    if (store.state.isInTransition) return;
    if (this.currentIndex === 0 && this.currentIndex === this.totalLength) return;

    const stateScroll = this.cursor.state === 'close';

    switch (event.which) {
      case 1:
        this.cursor.onMousedown(event, () => this.lockScroll(stateScroll));
        break;
      case 2:
        console.log('Why? 🤔');
        break;
      case 3:
        console.log('Are you a developer!? 👁');
        break;
      default:
        console.log('You are a weirdo 🛸');
    }
  }

  onMouseup() {
    if (store.state.isInTransition) return;
    if (this.currentIndex === 0 && this.currentIndex === this.totalLength) return;

    this.cursor.onMouseup();
  }

  scrollTo(i) {
    if (store.state.isInTransition) return;
    if (this.currentIndex === i) return;
    store.commit('SET_STATE_TRANSITION', true);

    this.changeIndexes(i);
    this.transitionSlider();
  }

  navigateTo(yearToNavigate) {
    const yearInt = parseInt(yearToNavigate, 10);
    store.commit('SET_STATE_TRANSITION', true);

    if (yearInt !== 2019 && yearInt !== 1990) this.cursor.toClose();

    const currentIndex = this.years.findIndex(year => year === yearInt);
    this.changeIndexes(currentIndex);

    if (yearInt === 2019) {
      this.transitionSlider().add(this.navigator.toScroll(), 'start+=1')
        .add(this.cursor.toNormal(), 'start+=1')
        .to(this.yearSlider.ctx, 0, { autoAlpha: 1 }, 'start+=1')
        .to(this.year, 0.5, { autoAlpha: 1, y: 0 }, 'start+=1');
    } else if (yearInt === 1990) {
      this.transitionSlider().add(this.navigator.enter(), 'start+=1')
        .add(this.cursor.toNormal(), 'start+=1')
        .to(this.yearSlider.ctx, 0, { autoAlpha: 1 }, 'start+=1')
        .to(this.year, 0.5, { autoAlpha: 1, y: 0 }, 'start+=1');
    } else {
      this.transitionSlider();
    }
  }

  toIndex() {
    if (this.currentIndex === 0) return;
    store.commit('SET_STATE_TRANSITION', true);
    if (store.state.scrollIsLocked) {
      this.changeIndexes(0);
      this.transitionSlider();
    } else {
      TweenMax.to('.app__view', 1, {
        y: -200,
        autoAlpha: 0,
        ease: Power3.easeInOut,
        onComplete: () => {
          this.navigateTo(1990);
        },
      });
    }
  }

  transitionSlider(callback = () => {}) {
    const isInitial = this.navigator.state === 'initial';

    const tl = new TimelineMax({
      onStart: () => {
        store.commit('SET_CURRENT_INDEX', this.currentIndex);
        router.push(store.state.currentPage.content.url);
      },
      onComplete: () => {
        store.commit('SET_STATE_TRANSITION', false);
        if (isInitial) {
          this.cursor.state = 'hold';
          this.cursor.tiltCaption('');
        }
        callback();
      },
    });

    tl.addLabel('start')
      .add(this.updateYear(), 'start')
      .add(this.navigator.animateProgress(this.currentIndex - 1), 'start')
      .add(this.yearSlider.animateSlide(this.prevIndex, this.currentIndex), 'start')
      .add(this.coreGroup.slideFromTo(this.prevIndex, this.currentIndex), 'start');

    if (isInitial) tl.add(this.navigator.toScroll(), 'start');
    if (this.currentIndex === 0 && !isInitial) tl.add(this.navigator.enter(), 'start');

    return tl;
  }

  pageAnimation(type) {
    const scrollVelocity = 1000;
    const deltaTime = (scrollVelocity * scrollbar.ref.amount.y) / scrollbar.ref.boundries.max.y;
    const tl = new TimelineMax({
      onComplete: () => {
        if (this.currentIndex !== 0 && this.currentIndex !== this.totalLength) store.commit('SET_STATE_TRANSITION', false);
      },
    });

    if (type === 'enter') {
      eventHub.$emit('pageIn');

      tl.addLabel('start')
        .add(animations.get('titleZoom')(document.querySelector('.page-item__title'), 'in'), 'start')
        .add(this.navigator.toPage(), 'start')
        .add(this.cursor.toClose(), 'start')
        .add(animations.get('buttonToClose'), 'start')
        .add(this.coreGroup.enterPage(this.currentIndex), 'start')
        .to(this.yearSlider.ctx, 1, { autoAlpha: 0 }, 'start')
        .to(this.year, 0.5, { autoAlpha: 0, y: -50 }, 'start');
    } else {
      eventHub.$emit('pageOut');

      tl.addLabel('start')
        .add(() => {
          scrollbar.ref.scrollTo({ y: 0 }, deltaTime < 0 ? 0 : deltaTime);
        }, 'start')
        .add(animations.get('titleZoom')(document.querySelector('.page-item__title'), 'out'), `start+=${deltaTime / 1000}`)
        .add(this.navigator.toScroll(), `start+=${(deltaTime / 1000) + 0.5}`)
        .add(this.cursor.toNormal(), 'start')
        .add(this.coreGroup.leavePage(this.currentIndex), 'start')
        .add(animations.get('buttonToDiscover'), `start+=${(deltaTime / 1000) + 0.5}`)
        .to(this.yearSlider.ctx, 0.5, { autoAlpha: 1 }, 'start')
        .to(this.year, 0.5, { autoAlpha: 1, y: 0 }, 'start');
    }
  }

  updateYear() {
    const toUpdate = this.years[this.currentIndex] - this.currentYear.value;

    return TweenMax.to(this.currentYear, 1, {
      value: `+=${toUpdate}`,
      onUpdate: () => {
        this.year.innerHTML = Math.round(this.currentYear.value);
      },
      onComplete: () => {
        this.currentYear.value = this.years[this.currentIndex];
      },
    });
  }

  render(time) {
    this.stage.render(time);
  }

  resize() {
    this.navigator.resize();
    this.stage.resize();
  }
}

export default new Interface();
